import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiClock,
  FiCopy,
  FiEye,
  FiFileText,
  FiHeart,
  FiMail,
  FiMoreHorizontal,
  FiShare2,
  FiUser,
  FiX,
} from 'react-icons/fi';
import {
  FaFacebookF,
  FaLinkedinIn,
  FaTelegramPlane,
  FaTwitter,
  FaWhatsapp,
} from 'react-icons/fa';
import { api } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import { prepareRichTextForDisplay, richTextToPlainText } from '../utils/richText';
import './ArticleDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const VISITOR_STORAGE_KEY = 'haryadArticleVisitorId';

const getVisitorId = () => {
  const current = localStorage.getItem(VISITOR_STORAGE_KEY);
  if (current) return current;
  const randomPart = window.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  const visitorId = `article-visitor-${randomPart}`;
  localStorage.setItem(VISITOR_STORAGE_KEY, visitorId);
  return visitorId;
};

const resolveImageUrl = (value) => (value && value.startsWith('/') ? `${API_URL}${value}` : value || '');

const normalizeArticle = (article) => ({
  ...article,
  id: article._id || article.id,
  imageUrl: resolveImageUrl(article.imageUrl),
});

const getArticleShareData = (article, t) => {
  const url = new URL(window.location.href);
  url.hash = '';
  const articleUrl = url.toString();
  const title = article?.title || t('articleDetail.defaultTitle');
  const excerpt = richTextToPlainText(article?.excerpt || '').trim();
  const shareText = excerpt ? `${title}\n${excerpt}` : title;

  return {
    articleUrl,
    title,
    shareText,
    networks: [
      {
        id: 'telegram',
        label: t('articleDetail.networks.telegram'),
        icon: FaTelegramPlane,
        url: `https://t.me/share/url?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(shareText)}`,
      },
      {
        id: 'whatsapp',
        label: t('articleDetail.networks.whatsapp'),
        icon: FaWhatsapp,
        url: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${articleUrl}`)}`,
      },
      {
        id: 'facebook',
        label: t('articleDetail.networks.facebook'),
        icon: FaFacebookF,
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`,
      },
      {
        id: 'linkedin',
        label: t('articleDetail.networks.linkedin'),
        icon: FaLinkedinIn,
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`,
      },
      {
        id: 'twitter',
        label: t('articleDetail.networks.twitter'),
        icon: FaTwitter,
        url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(title)}`,
      },
      {
        id: 'email',
        label: t('articleDetail.networks.email'),
        icon: FiMail,
        url: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${shareText}\n\n${articleUrl}`)}`,
        external: false,
      },
    ],
  };
};

const ArticleSocialLinks = ({ networks, onSelected, t }) => (
  <div className="article-social-share-grid">
    {networks.map(({ id, label, icon: Icon, url, external = true }) => (
      <a
        key={id}
        className={`article-social-share-link is-${id}`}
        href={url}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        onClick={onSelected}
        aria-label={t('articleDetail.shareOn', { network: label })}
      >
        <span><Icon aria-hidden="true" /></span>
        <small>{label}</small>
      </a>
    ))}
  </div>
);

const prepareArticleContent = (value, t) => {
  const html = prepareRichTextForDisplay(value, API_URL, { allowVideo: true });
  if (typeof document === 'undefined') return { html, headings: [] };

  const container = document.createElement('div');
  container.innerHTML = html;
  const headings = Array.from(container.querySelectorAll('h2, h3')).map((heading, index) => {
    const id = `article-section-${index + 1}`;
    heading.id = id;
    return {
      id,
      title: heading.textContent || t('articleDetail.section', { number: index + 1 }),
      level: heading.tagName.toLowerCase(),
    };
  });

  return { html: container.innerHTML, headings };
};

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    locale,
    localePath,
    formatDate: formatLocalizedDate,
    formatNumber,
    setPageAlternates,
  } = useLocale();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likeBusy, setLikeBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const recordedViewIds = useRef(new Set());
  const articleContentRef = useRef(null);
  const shareControlRef = useRef(null);
  const copyResetTimerRef = useRef(null);
  const visitorIdRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    visitorIdRef.current = getVisitorId();
    setLoading(true);
    setCopied(false);
    setShareMenuOpen(false);
    setReadingProgress(0);

    const fetchArticle = async () => {
      try {
        const [detailResponse, listResponse] = await Promise.all([
          api.get(`/api/articles/${id}?visitorId=${encodeURIComponent(visitorIdRef.current)}&locale=${encodeURIComponent(locale.code)}`),
          api.get(`/api/articles?locale=${encodeURIComponent(locale.code)}`),
        ]);

        if (cancelled) return;
        if (!detailResponse.success || !detailResponse.data) {
          setArticle(null);
          setRelatedArticles([]);
          return;
        }

        const normalized = normalizeArticle(detailResponse.data);
        setArticle(normalized);
        if (normalized.slug && normalized.slug !== id) {
          navigate(localePath(`/articles/${normalized.slug}`), { replace: true });
        }
        setPageAlternates(Object.fromEntries(
          Object.entries(normalized.alternates || {}).map(([code, slug]) => [
            code,
            `/${code}/articles/${slug}`,
          ])
        ));

        if (listResponse.success && Array.isArray(listResponse.data)) {
          const candidates = listResponse.data
            .filter((item) => String(item._id || item.id) !== String(normalized.id));
          const explicitIds = new Set((normalized.relatedArticles || []).map((item) => String(item?._id || item?.id || item)));
          const explicit = candidates.filter((item) => explicitIds.has(String(item._id || item.id)));
          const categoryFallback = candidates.filter((item) => (
            item.category === normalized.category && !explicitIds.has(String(item._id || item.id))
          ));
          setRelatedArticles([...explicit, ...categoryFallback].slice(0, 3).map(normalizeArticle));

          const byId = new Map(candidates.map((item) => [String(item._id || item.id), normalizeArticle(item)]));
          const resolvedInternalLinks = (normalized.internalLinks || [])
            .map((link) => ({ ...link, target: byId.get(String(link.article?._id || link.article?.id || link.article)) || null }))
            .filter((link) => link.target);
          if (resolvedInternalLinks.length) {
            setArticle((current) => current ? { ...current, resolvedInternalLinks } : current);
          }
        }

        if (!recordedViewIds.current.has(normalized.id)) {
          recordedViewIds.current.add(normalized.id);
          api.post(`/api/articles/${normalized.id}/view`, {})
            .then((viewResponse) => {
              if (!cancelled && viewResponse.data?.views !== undefined) {
                setArticle((current) => current ? { ...current, views: viewResponse.data.views } : current);
              }
            })
            .catch(() => recordedViewIds.current.delete(normalized.id));
        }
      } catch (_) {
        if (!cancelled) {
          setArticle(null);
          setRelatedArticles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchArticle();
    return () => {
      cancelled = true;
    };
  }, [id, locale.code, localePath, navigate, setPageAlternates]);

  useEffect(() => {
    if (!article?.title) return undefined;
    const previousTitle = document.title;
    let descriptionMeta = document.querySelector('meta[name="description"]');
    const createdDescription = !descriptionMeta;
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }
    let keywordsMeta = document.querySelector('meta[name="keywords"]');
    const createdKeywords = !keywordsMeta;
    if (!keywordsMeta) {
      keywordsMeta = document.createElement('meta');
      keywordsMeta.setAttribute('name', 'keywords');
      document.head.appendChild(keywordsMeta);
    }
    const previousDescription = descriptionMeta.getAttribute('content') || '';
    const previousKeywords = keywordsMeta.getAttribute('content') || '';
    const keywords = [article.primaryKeyword, ...(article.secondaryKeywords || [])].filter(Boolean).join(', ');
    document.title = article.seoTitle || article.title;
    descriptionMeta.setAttribute('content', article.seoDescription || richTextToPlainText(article.excerpt).slice(0, 160));
    if (keywords) keywordsMeta.setAttribute('content', keywords);
    return () => {
      document.title = previousTitle;
      if (createdDescription) descriptionMeta.remove();
      else descriptionMeta.setAttribute('content', previousDescription);
      if (createdKeywords) keywordsMeta.remove();
      else keywordsMeta.setAttribute('content', previousKeywords);
    };
  }, [article]);

  useEffect(() => {
    if (!article?.title || typeof document === 'undefined') return undefined;
    const requestedSchemas = Array.isArray(article.schemaTypes) ? article.schemaTypes : [];
    const primaryType = requestedSchemas.includes('NewsArticle')
      ? 'NewsArticle'
      : requestedSchemas.includes('TechArticle') ? 'TechArticle' : 'Article';
    const currentUrl = window.location.href.split('#')[0];
    const articleSchema = {
      '@type': primaryType,
      '@id': `${currentUrl}#article`,
      headline: article.seoTitle || article.title,
      description: article.seoDescription || richTextToPlainText(article.excerpt).slice(0, 220),
      datePublished: article.publishedAt || article.createdAt || undefined,
      dateModified: article.updatedAt || article.createdAt || undefined,
      mainEntityOfPage: currentUrl,
      author: { '@type': 'Person', name: article.author?.name || 'Haryad' },
      publisher: { '@type': 'Organization', name: 'Haryad' },
      keywords: [article.primaryKeyword, ...(article.secondaryKeywords || [])].filter(Boolean).join(', ') || undefined,
      image: article.imageUrl || undefined,
    };
    const graph = [articleSchema];
    if (requestedSchemas.includes('BreadcrumbList')) {
      graph.push({
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Haryad', item: `${window.location.origin}${localePath('/')}` },
          { '@type': 'ListItem', position: 2, name: t('articles.name'), item: `${window.location.origin}${localePath('/articles')}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: currentUrl },
        ],
      });
    }
    if ((requestedSchemas.includes('FAQPage') || (article.faq || []).length) && (article.faq || []).length) {
      graph.push({
        '@type': 'FAQPage',
        mainEntity: article.faq
          .filter((item) => (item.question || item.q) && (item.answer || item.a))
          .map((item) => ({
            '@type': 'Question',
            name: item.question || item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.answer || item.a },
          })),
      });
    }
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.articleSchema = 'true';
    script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
    document.head.appendChild(script);
    return () => script.remove();
  }, [article, localePath, t]);

  useEffect(() => {
    if (!shareMenuOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (shareControlRef.current && !shareControlRef.current.contains(event.target)) {
        setShareMenuOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setShareMenuOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [shareMenuOpen]);

  useEffect(() => () => window.clearTimeout(copyResetTimerRef.current), []);

  useEffect(() => {
    const updateProgress = () => {
      const element = articleContentRef.current;
      if (!element) return;
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const readableDistance = Math.max(element.offsetHeight - window.innerHeight * 0.45, 1);
      const travelled = window.scrollY + window.innerHeight * 0.2 - elementTop;
      setReadingProgress(Math.min(100, Math.max(0, (travelled / readableDistance) * 100)));
    };

    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    return () => {
      window.removeEventListener('scroll', updateProgress);
      window.removeEventListener('resize', updateProgress);
    };
  }, [article]);

  const contentData = useMemo(
    () => prepareArticleContent(article?.content || article?.excerpt || '', t),
    [article?.content, article?.excerpt, t]
  );
  const readingMinutes = useMemo(() => {
    const wordCount = richTextToPlainText(article?.content || article?.excerpt || '').split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(wordCount / 220));
  }, [article?.content, article?.excerpt]);

  const copyArticleLink = async () => {
    const shareUrl = new URL(window.location.href);
    shareUrl.hash = '';

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl.toString());
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl.toString();
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      setCopied(true);
      window.clearTimeout(copyResetTimerRef.current);
      copyResetTimerRef.current = window.setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      setCopied(false);
    }
  };

  const shareWithDevice = async () => {
    if (!navigator.share || !article) return;
    const { articleUrl, title, shareText } = getArticleShareData(article, t);

    try {
      await navigator.share({ title, text: shareText, url: articleUrl });
      setShareMenuOpen(false);
    } catch (error) {
      if (error.name !== 'AbortError') setShareMenuOpen(true);
    }
  };

  const toggleLike = async () => {
    if (!article || likeBusy) return;
    const nextLiked = !article.liked;
    setLikeBusy(true);
    try {
      const response = await api.post(`/api/articles/${article.id}/like`, {
        visitorId: visitorIdRef.current || getVisitorId(),
        liked: nextLiked,
      });
      if (response.success && response.data) {
        setArticle((current) => current ? {
          ...current,
          liked: response.data.liked,
          likesCount: response.data.likesCount,
        } : current);
      }
    } finally {
      setLikeBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="article-detail-page">
        <div className="article-detail-state">{t('articles.loading')}</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="article-detail-page">
        <div className="article-detail-state article-detail-not-found">
          <FiFileText aria-hidden="true" />
          <h1>{t('articleDetail.missing')}</h1>
          <p>{t('articleDetail.missingHint')}</p>
          <Link to={localePath('/articles')}><FiArrowRight aria-hidden="true" /> {t('nav.articles')}</Link>
        </div>
      </div>
    );
  }

  const authorName = article.author?.name || t('articleDetail.editorial');
  const publishedAt = formatLocalizedDate(article.createdAt || article.date, { year: 'numeric', month: 'long', day: 'numeric' });
  const updatedAt = formatLocalizedDate(article.updatedAt || article.createdAt, { year: 'numeric', month: 'long', day: 'numeric' });
  const shareData = getArticleShareData(article, t);

  return (
    <div className="article-detail-page">
      <div className="article-reading-progress" aria-hidden="true">
        <span style={{ width: `${readingProgress}%` }} />
      </div>

      <div className="article-detail-container">
        <Link to={localePath('/articles')} className="article-detail-breadcrumb">
          <FiArrowRight aria-hidden="true" /> {t('articleDetail.back')}
        </Link>

        <header className={`article-detail-hero ${article.imageUrl ? '' : 'no-image'}`}>
          {article.imageUrl ? (
            <img src={article.imageUrl} alt={article.imageAlt || article.title} />
          ) : (
            <div className="article-detail-hero-placeholder"><FiFileText aria-hidden="true" /></div>
          )}
          <div className="article-detail-hero-overlay" />
          <div className="article-detail-hero-content">
            <span className="article-detail-category">{article.category}</span>
            <h1>{article.title}</h1>
            <div className="article-detail-meta">
              <span><FiUser aria-hidden="true" /> {authorName}</span>
              <span><FiCalendar aria-hidden="true" /> {publishedAt}</span>
              <span><FiClock aria-hidden="true" /> {t('articles.readingTime', { count: formatNumber(readingMinutes) })}</span>
              <span><FiEye aria-hidden="true" /> {t('articles.views', { count: formatNumber(article.views || 0) })}</span>
            </div>
          </div>
        </header>

        <div className="article-detail-layout">
          <main className="article-reading-card" ref={articleContentRef}>
            <p className="article-detail-excerpt">{article.excerpt}</p>


            <div className="article-detail-actions" aria-label={t('articleDetail.tools')}>
              <button
                type="button"
                className={article.liked ? 'is-liked' : ''}
                onClick={toggleLike}
                disabled={likeBusy}
                aria-pressed={Boolean(article.liked)}
              >
                <FiHeart aria-hidden="true" />
                {article.liked ? t('articleDetail.liked') : t('articleDetail.like')}
                <b>{formatNumber(article.likesCount || 0)}</b>
              </button>
              <div className="article-share-control" ref={shareControlRef}>
                <button
                  type="button"
                  className="article-share-trigger"
                  onClick={() => setShareMenuOpen((current) => !current)}
                  aria-haspopup="dialog"
                  aria-expanded={shareMenuOpen}
                >
                  <FiShare2 aria-hidden="true" /> {t('articleDetail.share')}
                </button>

                {shareMenuOpen && (
                  <section className="article-social-share-panel" role="dialog" aria-label={t('articleDetail.shareTitle')}>
                    <div className="article-social-share-heading">
                      <div>
                        <strong>{t('articleDetail.shareTitle')}</strong>
                        <small>{t('articleDetail.shareHint')}</small>
                      </div>
                      <button type="button" className="ui-close-button" onClick={() => setShareMenuOpen(false)} aria-label={t('articleDetail.closeShare')}>
                        <FiX aria-hidden="true" />
                      </button>
                    </div>

                    <ArticleSocialLinks
                      networks={shareData.networks}
                      t={t}
                      onSelected={() => setShareMenuOpen(false)}
                    />

                    <div className="article-social-share-secondary">
                      <button type="button" onClick={copyArticleLink}>
                        {copied ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
                        {copied ? t('articleDetail.copied') : t('articleDetail.copy')}
                      </button>
                      {navigator.share && (
                        <button type="button" onClick={shareWithDevice}>
                          <FiMoreHorizontal aria-hidden="true" /> {t('articleDetail.otherApps')}
                        </button>
                      )}
                    </div>
                  </section>
                )}
              </div>
              <button type="button" onClick={copyArticleLink}>
                {copied ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
                {copied ? t('articleDetail.copied') : t('articleDetail.copy')}
              </button>
            </div>

            <div
              className="article-rich-content"
              dangerouslySetInnerHTML={{ __html: contentData.html }}
            />

            {(article.resolvedInternalLinks || []).length > 0 && (
              <section className="article-ai-internal-links">
                <span>{t('articleDetail.continueReadingEyebrow')}</span>
                <h2>{t('articleDetail.continueReading')}</h2>
                <div>
                  {article.resolvedInternalLinks.map((link, index) => (
                    <Link key={`${link.target.id}-${index}`} to={localePath(`/articles/${link.target.slug || link.target.id}`)}>
                      <strong>{link.anchorText || link.target.title}</strong>
                      {link.reason && <small>{link.reason}</small>}
                      <FiArrowLeft aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {(article.faq || []).length > 0 && (
              <section className="article-ai-faq" aria-labelledby="article-faq-title">
                <span>{t('articleDetail.faqEyebrow')}</span>
                <h2 id="article-faq-title">{t('articleDetail.faq')}</h2>
                <div>
                  {article.faq.map((item, index) => (
                    <article key={`faq-${index}`}>
                      <h3>{item.question || item.q}</h3>
                      <p>{item.answer || item.a}</p>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {article.cta?.label && article.cta?.url && (
              <section className="article-ai-cta">
                <div>
                  {article.cta.title && <h2>{article.cta.title}</h2>}
                  {article.cta.text && <p>{article.cta.text}</p>}
                </div>
                {String(article.cta.url).startsWith('/') ? (
                  <Link to={article.cta.url}>{article.cta.label} <FiArrowLeft aria-hidden="true" /></Link>
                ) : (
                  <a href={article.cta.url} target="_blank" rel="noopener noreferrer">{article.cta.label} <FiArrowLeft aria-hidden="true" /></a>
                )}
              </section>
            )}

            {(article.aiSources || []).length > 0 && (
              <section className="article-ai-sources">
                <span>{t('articleDetail.sourcesEyebrow')}</span>
                <h2>{t('articleDetail.sources')}</h2>
                <ul>
                  {article.aiSources.map((source, index) => (
                    <li key={`${source.url}-${index}`}>
                      <a href={source.url} target="_blank" rel="noopener noreferrer">{source.title || source.url}</a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <footer className="article-content-footer">
              <div>
                <span>{t('articleDetail.updated')}</span>
                <strong>{updatedAt}</strong>
              </div>
              <button type="button" className={article.liked ? 'is-liked' : ''} onClick={toggleLike} disabled={likeBusy}>
                <FiHeart aria-hidden="true" /> {t('articleDetail.useful')}
              </button>
            </footer>
          </main>

          <aside className="article-detail-sidebar">
            <section className="article-author-card">
              <span className="article-author-avatar">
                {article.author?.avatar ? <img src={resolveImageUrl(article.author.avatar)} alt={authorName} /> : <FiUser aria-hidden="true" />}
              </span>
              <div>
                <small>{t('articleDetail.author')}</small>
                <strong>{authorName}</strong>
                <p>{t('articleDetail.authorBio')}</p>
              </div>
            </section>

            {contentData.headings.length > 0 && (
              <nav className="article-toc" aria-label={t('articleDetail.toc')}>
                <h2>{t('articleDetail.toc')}</h2>
                <ol>
                  {contentData.headings.map((heading) => (
                    <li key={heading.id} className={heading.level === 'h3' ? 'is-subheading' : ''}>
                      <a href={`#${heading.id}`}>{heading.title}</a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

            <section className="article-sidebar-share">
              <h2>{t('articleDetail.shareSidebar')}</h2>
              <p>{t('articleDetail.shareSidebarHint')}</p>
              <ArticleSocialLinks networks={shareData.networks} t={t} />
              <button type="button" className="article-sidebar-copy" onClick={copyArticleLink}>
                {copied ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
                {copied ? t('articleDetail.copied') : t('articleDetail.copy')}
              </button>
            </section>
          </aside>
        </div>

        {relatedArticles.length > 0 && (
          <section className="related-articles">
            <div className="related-articles-heading">
              <div>
                <span>{t('articleDetail.relatedEyebrow')}</span>
                <h2>{t('articleDetail.related')}</h2>
              </div>
              <Link to={localePath('/articles')}>{t('articleDetail.viewAll')} <FiArrowLeft aria-hidden="true" /></Link>
            </div>
            <div className="related-articles-grid">
              {relatedArticles.map((related) => (
                <Link key={related.id} to={localePath(`/articles/${related.slug || related.id}`)} className="related-article-card">
                  <div>
                    {related.imageUrl ? <img src={related.imageUrl} alt={related.imageAlt || related.title} loading="lazy" /> : <FiFileText aria-hidden="true" />}
                  </div>
                  <span>{related.category}</span>
                  <h3>{related.title}</h3>
                  <small>{formatLocalizedDate(related.createdAt || related.date, { year: 'numeric', month: 'long', day: 'numeric' })}</small>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ArticleDetail;
